import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import {
  useWorkAssignment,
  useUpdateWorkAssignment,
  useCancelWorkAssignment,
  useCreateViolationFromAssignment,
  isAssignmentComposable,
} from "@/hooks/use-work-assignments";
import { useTemplateVersions } from "@/hooks/use-form-template";
import { useFormSubmissions } from "@/hooks/use-form-submissions";
import { AssignmentStatusBadge } from "@/components/work-assignments/assignment-status-badge";
import {
  AssigneeCombobox,
  type AssigneeSelection,
} from "@/components/work-assignments/assignee-combobox";
import { CancelAssignmentDialog } from "@/components/work-assignments/cancel-assignment-dialog";
import { workAssignmentAssigneeErrorKey } from "@/components/work-assignments/assignee-error-key";
import { OriginPill } from "@/components/work-assignments/origin-pill";
import { CreateViolationPanel } from "@/components/work-assignments/create-violation-panel";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { useOpenTesterThread } from "@/hooks/use-open-tester-thread";
import { formatDateOnly, formatTimestampDate, todayDateInputValue } from "@/lib/dates";
import {
  PageHeader,
  Breadcrumbs,
  Badge,
  Button,
  Card,
  EmptyState,
  EmptyValue,
  FormField,
  inputClass,
  PageSkeleton,
} from "@/components/ui";

export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute("/_authenticated/test-activity/assignments/$id/")({
  component: WorkAssignmentDetailRoute,
});

/**
 * Param gate. Keeps the detail page's hooks off a segment that cannot be an
 * id, so a dead path costs no request.
 */
function WorkAssignmentDetailRoute() {
  const { id } = Route.useParams();
  if (!UUID_RE.test(id)) return <WorkAssignmentNotFound />;
  return <WorkAssignmentDetailPage />;
}

/**
 * `/test-activity/assignments/<anything>` matches this route, so a dead path used to
 * spend a 404 on the API and then claim the DETAIL READ failed, which reads as
 * a transient outage rather than a URL that means nothing.
 *
 * Rendered here rather than thrown as the router's `notFound()`: a route's own
 * `notFoundComponent` does not catch a not-found its own `beforeLoad` threw
 * (the nearest ANCESTOR boundary does), and this app registers no ancestor
 * boundary, so `notFound()` paints a blank page. Rendering in place also keeps
 * the nav shell around the message.
 */
function WorkAssignmentNotFound() {
  const { t } = useTranslation(["work-assignments", "common"]);
  return (
    <div className="p-8">
      <EmptyState title={t("detail.notFound.title")} description={t("detail.notFound.body")} />
      <div className="mt-4">
        <Link
          to="/test-activity"
          search={{ tab: "assignments" }}
          className="text-sm text-primary hover:underline"
        >
          {t("common:back")}
        </Link>
      </div>
    </div>
  );
}

const dateFormat: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
};

/**
 * For full ISO timestamps (created_at) only. The date-only due_date renders
 * via formatDateOnly, which parses as local time so the calendar day never
 * shifts across timezones (`new Date("YYYY-MM-DD")` parses as UTC midnight
 * and renders the previous day in negative-offset timezones).
 */
function formatTimestamp(value: string): string {
  return new Date(value).toLocaleDateString(undefined, dateFormat);
}

/**
 * Assignment detail: target card (facility / installation drill-ins),
 * assignment card (template + pinned version, origin, the requester's
 * preferred date + note when a regulated party asked for the work, due date,
 * status), the utility↔tester conversation hand-off,
 * submission link when one exists, org_admin reassign card, and the
 * lifecycle actions — Cancel (any non-terminal status) and the manual
 * violation promotion for overdue assignments. Terminal statuses
 * (completed / cancelled) hide the mutating affordances; the rows stay
 * readable as historical record.
 */
function WorkAssignmentDetailPage() {
  const { t } = useTranslation(["work-assignments", "common", "nav"]);
  const toasts = useToast();
  const navigate = useNavigate();
  const { id } = Route.useParams();

  const { can } = usePermissions();
  const canReassign = can("work_assignments", "update");
  const canPromote = can("violations", "promote");
  const canCompleteTest = can("form_submissions", "create");
  const canMessageTester = can("messages", "read");

  const { data: assignment, isLoading, isError } = useWorkAssignment(id);
  const versionsQuery = useTemplateVersions(assignment?.template_id);
  const submissionsQuery = useFormSubmissions({ assignment_id: id, limit: 1 });

  const updateMutation = useUpdateWorkAssignment(id);
  const cancelMutation = useCancelWorkAssignment(id);
  const openThread = useOpenTesterThread();
  const promoteMutation = useCreateViolationFromAssignment(id);

  const [editDueDate, setEditDueDate] = useState("");
  const [editAssignee, setEditAssignee] = useState<AssigneeSelection | null>(null);
  const [editSeeded, setEditSeeded] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [assigneeError, setAssigneeError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [violationPanelDismissed, setViolationPanelDismissed] = useState(false);

  // Seed the reassign form once from the loaded row; later renders keep the
  // operator's in-progress edits. A company-pool row (assigned_to NULL +
  // assigned_to_company_id set) seeds a company selection so reassign opens on
  // the current company; otherwise it seeds the assigned person (if any).
  useEffect(() => {
    if (assignment && !editSeeded) {
      setEditDueDate(assignment.due_date);
      if (assignment.assigned_to) {
        setEditAssignee({ kind: "person", profileId: assignment.assigned_to, name: "" });
      } else if (assignment.assigned_to_company_id) {
        setEditAssignee({
          kind: "company",
          companyId: assignment.assigned_to_company_id,
          name: assignment.assigned_to_company_name ?? "",
        });
      } else {
        setEditAssignee(null);
      }
      setEditSeeded(true);
    }
  }, [assignment, editSeeded]);

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !assignment) {
    return (
      <div className="p-8">
        <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">
          {t("errors.loadDetailFailed")}
        </div>
        <div className="mt-4">
          <Link
            to="/test-activity"
            search={{ tab: "assignments" }}
            className="text-sm text-primary hover:underline"
          >
            {t("common:back")}
          </Link>
        </div>
      </div>
    );
  }

  const isTerminal = assignment.status === "completed" || assignment.status === "cancelled";
  // The composer entry point only makes sense for the three open,
  // not-yet-submitted statuses -- a `submitted` row already has a submission
  // of record even though it isn't `isTerminal` above.
  const composable = isAssignmentComposable(assignment.status);
  const showCompleteTestHint = composable && !canCompleteTest;
  // Denormalized labels ride the WorkAssignment read projection (LEFT JOINs
  // server-side); a raw UUID in these cells is unacceptable UX for compliance
  // software, so the read resolves the name/serial/title inline.
  const facilityName = assignment.facility_name;
  const installationSerial = assignment.device_serial_number;
  const templateTitle = assignment.template_title;
  const pinnedVersion = (versionsQuery.data?.items ?? []).find(
    (v) => v.id === assignment.template_version_id,
  );
  const submission = submissionsQuery.data?.items?.[0];

  // For the seeded person (the currently-assigned one, whose pick-time name is
  // empty), the display name is the denormalized `assignee_name` off the row;
  // a freshly-picked person carries its own name from the combobox. A company
  // selection carries its own name (seeded from the row's
  // assigned_to_company_name or picked from the registry list).
  const editAssigneeDisplay: AssigneeSelection | null =
    editAssignee?.kind === "person"
      ? {
          kind: "person",
          profileId: editAssignee.profileId,
          name:
            editAssignee.profileId === assignment.assigned_to
              ? (assignment.assignee_name ?? editAssignee.name)
              : editAssignee.name,
        }
      : editAssignee;

  // A cadence row follows the device's test policy: the API refuses a new due
  // date, so the field is read-only and the save never sends it; and a
  // cancelled cadence row is re-issued by the next sweep while the device
  // stays due, which the cancel dialog says.
  const cadenceRow = assignment.origin === "cadence";

  const handleSave = () => {
    setEditError(null);
    setAssigneeError(null);
    // Three-state Pattern A clear-the-other: a person reassign sets
    // assigned_to and explicit-null clears assigned_to_company_id; a company
    // dispatch does the reverse; clearing the assignee nulls both. Explicit
    // null is the wire signal that unassigns (absent would preserve).
    updateMutation.mutate(
      {
        ...(cadenceRow ? {} : { due_date: editDueDate }),
        assigned_to: editAssignee?.kind === "person" ? editAssignee.profileId : null,
        assigned_to_company_id: editAssignee?.kind === "company" ? editAssignee.companyId : null,
      },
      {
        onSuccess: () => {
          toasts.success("work-assignments:detail.saveSuccess");
        },
        onError: (err) => {
          const assigneeKey = workAssignmentAssigneeErrorKey(err);
          if (assigneeKey) {
            setAssigneeError(t(assigneeKey));
          } else if (err instanceof ApiError && err.status === 409) {
            setEditError(t("errors.terminalStatus409"));
          } else {
            setEditError(t("errors.updateFailed"));
          }
        },
      },
    );
  };

  // Get-or-create this work item's utility thread, then hand off to the staff
  // inbox on the frozen deep link. The inbox reads the transcript by this id,
  // which is the read that marks the thread read.
  const handleMessageTester = () => {
    openThread.mutate(
      { assignmentId: id },
      {
        onSuccess: (thread) => {
          void navigate({ to: "/messages", search: { thread: thread.id } });
        },
        onError: (err) => toasts.apiError(err.code),
      },
    );
  };

  const handleCancelConfirm = () => {
    setCancelError(null);
    cancelMutation.mutate(undefined, {
      onSuccess: () => {
        toasts.success("work-assignments:detail.cancel.success");
        setCancelOpen(false);
      },
      onError: (err) => {
        setCancelError(
          err instanceof ApiError && err.status === 409
            ? t("errors.terminalStatus409")
            : t("errors.cancelFailed"),
        );
        setCancelOpen(false);
      },
    });
  };

  // Captured as a const so the `violationId &&` guard narrows inside the
  // button's own callback, which a property access does not survive.
  const violationId = assignment.violation_id;

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          {
            label: t("testActivity.assignments", { ns: "nav" }),
            to: "/test-activity",
            search: { tab: "assignments" },
          },
          { label: t("detail.title") },
        ]}
      />
      <PageHeader
        title={t("detail.title")}
        actions={
          <div className="flex items-center gap-3">
            {violationId && (
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate({
                    to: "/test-activity/violations/$id",
                    params: { id: violationId },
                  })
                }
              >
                {t("detail.viewViolation")}
              </Button>
            )}
            {canMessageTester && (
              <Button
                variant="secondary"
                onClick={handleMessageTester}
                disabled={openThread.isPending}
              >
                {t("detail.messageTester")}
              </Button>
            )}
            {!isTerminal && (
              <Button variant="danger" onClick={() => setCancelOpen(true)}>
                {t("detail.cancel.cta")}
              </Button>
            )}
            {composable && canCompleteTest && (
              <Button
                onClick={() =>
                  void navigate({
                    to: "/test-entry/$assignmentId",
                    params: { assignmentId: assignment.id },
                  })
                }
              >
                {t("detail.completeTest")}
              </Button>
            )}
          </div>
        }
      />

      {showCompleteTestHint && (
        <p className="mt-2 text-sm text-muted">{t("detail.completeTestHint")}</p>
      )}

      {cancelError && (
        <div className="mt-4 rounded-md bg-danger/10 p-4 text-sm text-danger">{cancelError}</div>
      )}

      <div className="mt-6 space-y-6">
        <Card title={t("detail.cards.target")}>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted">{t("columns.facility")}</dt>
              <dd className="mt-0.5">
                <Link
                  to="/facilities/$facilityId"
                  params={{ facilityId: assignment.facility_id }}
                  className="text-primary hover:underline"
                >
                  {facilityName ?? <EmptyValue />}
                </Link>
              </dd>
            </div>
            <div>
              <dt className="text-muted">{t("columns.installation")}</dt>
              <dd className="mt-0.5">
                {assignment.device_installation_id ? (
                  <Link
                    to="/device-installations/$id"
                    params={{ id: assignment.device_installation_id }}
                    className="font-mono text-xs text-primary hover:underline"
                  >
                    {installationSerial ?? <EmptyValue />}
                  </Link>
                ) : (
                  <EmptyValue />
                )}
              </dd>
            </div>
          </dl>
        </Card>

        <Card title={t("detail.cards.assignment")}>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted">{t("columns.template")}</dt>
              <dd className="mt-0.5">
                <Link
                  to="/test-program/templates/$id"
                  params={{ id: assignment.template_id }}
                  className="text-primary hover:underline"
                >
                  {templateTitle ?? <EmptyValue />}
                </Link>
              </dd>
            </div>
            <div>
              <dt className="text-muted">{t("detail.version")}</dt>
              <dd className="mt-0.5 text-on-surface">
                {pinnedVersion ? `v${pinnedVersion.version}` : <EmptyValue />}
              </dd>
            </div>
            <div>
              <dt className="text-muted">{t("columns.origin")}</dt>
              <dd className="mt-0.5">
                <OriginPill origin={assignment.origin} />
              </dd>
            </div>
            {assignment.preferred_date && (
              <div>
                <dt className="text-muted">{t("detail.preferredDate")}</dt>
                <dd className="mt-0.5 text-on-surface">
                  {formatTimestampDate(assignment.preferred_date, dateFormat)}
                </dd>
              </div>
            )}
            {assignment.customer_note && (
              <div className="sm:col-span-2">
                <dt className="text-muted">{t("detail.customerNote")}</dt>
                <dd className="mt-0.5 whitespace-pre-wrap text-on-surface">
                  {assignment.customer_note}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-muted">{t("columns.dueDate")}</dt>
              <dd className="mt-0.5 text-on-surface">
                <span className="inline-flex items-center gap-2">
                  {formatDateOnly(assignment.due_date, dateFormat)}
                  {assignment.overdue && <Badge variant="danger">{t("list.overdueBadge")}</Badge>}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-muted">{t("columns.status")}</dt>
              <dd className="mt-0.5">
                <AssignmentStatusBadge status={assignment.status} />
              </dd>
            </div>
            <div>
              <dt className="text-muted">{t("detail.created")}</dt>
              <dd className="mt-0.5 text-on-surface">{formatTimestamp(assignment.created_at)}</dd>
            </div>
          </dl>
        </Card>

        {submission && (
          <Card title={t("detail.cards.submission")}>
            <Button
              variant="secondary"
              onClick={() =>
                void navigate({
                  to: "/test-activity/submissions/$id",
                  params: { id: submission.id },
                })
              }
            >
              {t("detail.viewSubmission")}
            </Button>
          </Card>
        )}

        {canReassign && !isTerminal && (
          <Card title={t("detail.reassign")}>
            <div className="space-y-4">
              <FormField label={t("columns.assignee")} error={assigneeError ?? undefined}>
                <AssigneeCombobox value={editAssigneeDisplay} onChange={setEditAssignee} />
              </FormField>
              <FormField
                label={t("detail.dueDate")}
                hint={cadenceRow ? t("detail.dueDateFollowsDevice") : undefined}
              >
                <input
                  type="date"
                  value={editDueDate}
                  onChange={(e) => setEditDueDate(e.target.value)}
                  disabled={cadenceRow}
                  className={`${inputClass} disabled:opacity-50`}
                />
              </FormField>
              {editError && (
                <div className="rounded-md bg-danger/10 p-3 text-sm text-danger">{editError}</div>
              )}
              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={updateMutation.isPending}>
                  {t("detail.save")}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {canPromote &&
          assignment.overdue &&
          !assignment.violation_id &&
          !isTerminal &&
          !violationPanelDismissed && (
            <CreateViolationPanel
              targetKind={assignment.target_kind}
              // An overdue assignment's case is discovered today — the local
              // calendar day the coordinator promotes it.
              discoveredDate={todayDateInputValue()}
              mutation={promoteMutation}
              onClose={() => setViolationPanelDismissed(true)}
            />
          )}
      </div>

      <CancelAssignmentDialog
        open={cancelOpen}
        onConfirm={handleCancelConfirm}
        onDismiss={() => setCancelOpen(false)}
        cadence={cadenceRow}
      />
    </div>
  );
}
